import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "../../utils/toast";
import {
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  KeyRound,
  Loader2,
  Phone,
  ShieldPlus,
  UserRound,
} from "lucide-react";
import { createPatient } from "../../store/slices/patientSlice";
import { fetchTests } from "../../store/slices/testSlice";
import { createUser } from "../../store/slices/userSlice";

const PatientRegistration = ({ onPatientRegistered }) => {
  const dispatch = useDispatch();
  const { loading: patientLoading } = useSelector((state) => state.patients);

  const [formData, setFormData] = useState({
    full_name: "",
    phone: "",
    age: "",
    date_of_birth: "",
    gender: "",
    address: "",
    emergency_contact: "",
    email: "",
    blood_type: "",
    known_allergies: "",
    chronic_conditions: "",
    current_medications: "",
  });

  const [cardNumber, setCardNumber] = useState(
    "CLN-" + Math.floor(1000 + Math.random() * 9000)
  );

  const [touched, setTouched] = useState({});
  const [medicalOpen, setMedicalOpen] = useState(false);

  // Fetch tests on component mount
  useEffect(() => {
    dispatch(fetchTests());
  }, [dispatch]);

  const bloodTypes = [
    "A+",
    "A-",
    "B+",
    "B-",
    "AB+",
    "AB-",
    "O+",
    "O-",
    "unknown",
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.full_name || !formData.phone) {
      toast.error("Please fill in required fields: Full Name and Phone");
      return;
    }
    let newUserId = null;
    const tempPassword = (formData.phone || "").trim() || cardNumber; // fallback: use cardNumber if phone is empty

    try {
      const userPayload = {
        username: cardNumber,
        email: formData.email || null,
        phone: formData.phone,
        full_name: formData.full_name,
        role: "patient",
        password: tempPassword,
        is_active: 1,
      };

      const createdUser = await dispatch(createUser(userPayload)).unwrap();
      // unwrap may return the entity directly or {data: {...}}
      const normalizedUser = createdUser?.data ?? createdUser;
      newUserId = normalizedUser?.id;

      if (!newUserId) {
        toast.error("Failed to create user account");
        return;
      }
    } catch (err) {
      const msg = err?.message || "Failed to create user account";
      toast.error(msg);
      return;
    }

    try {
      // Create patient
      const patientPayload = {
        ...formData,
        card_number: cardNumber,
        user_id: newUserId,
      };

      const result = await dispatch(createPatient(patientPayload)).unwrap();

      // Normalize result to the patient object shape
      const createdPatient = result?.data ?? result;

      // Reflect server-assigned card number in UI
      setCardNumber(createdPatient.card_number || cardNumber);
      toast.success("Patient registered successfully!");

      // Move to step 2 and pass patient data
      if (onPatientRegistered) {
        onPatientRegistered(createdPatient);
      }
    } catch (error) {
      console.error("Failed to register patient:", error);
      toast.error("Failed to register patient");
    }
  };

  const isLoading = patientLoading;

  const fullNameTrimmed = String(formData.full_name || "").trim();
  const phoneTrimmed = String(formData.phone || "").trim();
  const errors = {
    full_name: fullNameTrimmed ? "" : "Full name is required.",
    phone: phoneTrimmed ? "" : "Phone number is required.",
  };

  const fieldBase =
    "w-full rounded-xl bg-white px-3.5 py-2.5 text-sm text-gray-900 ring-1 ring-gray-200 placeholder:text-gray-400 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[#36F1A2]";
  const fieldError = "ring-2 ring-red-200 focus:ring-red-300";
  const labelBase = "text-sm font-semibold text-gray-900";
  const errorText = "mt-1 text-xs font-medium text-red-600";

  const markTouched = (key) =>
    setTouched((prev) => ({ ...prev, [key]: true }));

  return (
    <div className="pt-6">
      <form onSubmit={handleSubmit}>
        <div className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70">
          <div className="border-t-4 border-[#36F1A2] bg-gradient-to-r from-gray-50 to-white px-5 py-4 sm:px-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-2 rounded-full bg-[#36F1A2]/15 px-3 py-1 text-xs font-semibold text-[#235F72] ring-1 ring-[#36F1A2]/40">
                  <UserRound className="h-4 w-4" />
                  Step 1 of 2
                </div>
                <h2 className="mt-2 text-xl font-semibold text-gray-900">Register new patient</h2>
                <p className="mt-1 text-sm text-gray-600">Required fields are marked with an asterisk.</p>
              </div>

              <div className="flex items-center justify-between gap-3 rounded-2xl bg-white/70 px-4 py-3 ring-1 ring-gray-200 backdrop-blur">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Card Number
                </div>
                <div className="rounded-full bg-[#235F72] px-3 py-1 text-sm font-semibold text-white shadow-sm">
                  {cardNumber}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-5 px-5 py-5 sm:px-6">
            <div className="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="min-w-0 lg:col-span-2 space-y-6">
                {/* Personal info */}
                <section className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200/70">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                        <ClipboardCheck className="h-4 w-4 text-[#235F72]" />
                        Personal information
                      </div>
                      <div className="mt-1 text-xs text-gray-600">Identity and basic demographics.</div>
                    </div>
                    <div className="hidden sm:flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs text-gray-600 ring-1 ring-gray-200">
                      <span className="text-red-600">*</span>
                      Required
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className={labelBase} htmlFor="full_name">
                        Full name <span className="text-red-600">*</span>
                      </label>
                      <input
                        id="full_name"
                        type="text"
                        value={formData.full_name}
                        onChange={(e) =>
                          setFormData({ ...formData, full_name: e.target.value })
                        }
                        onBlur={() => markTouched("full_name")}
                        className={
                          fieldBase +
                          (touched.full_name && errors.full_name ? ` ${fieldError}` : "")
                        }
                        placeholder="Enter full name"
                        required
                        aria-invalid={touched.full_name && Boolean(errors.full_name)}
                      />
                      {touched.full_name && errors.full_name ? (
                        <p className={errorText}>{errors.full_name}</p>
                      ) : null}
                    </div>

                    <div>
                      <label className={labelBase} htmlFor="phone">
                        Phone number <span className="text-red-600">*</span>
                      </label>
                      <div className="relative">
                        <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                          <Phone className="h-4 w-4" />
                        </span>
                        <input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) =>
                            setFormData({ ...formData, phone: e.target.value })
                          }
                          onBlur={() => markTouched("phone")}
                          className={
                            "pl-10 " +
                            fieldBase +
                            (touched.phone && errors.phone ? ` ${fieldError}` : "")
                          }
                          placeholder="+251 91 234 5678"
                          required
                          aria-invalid={touched.phone && Boolean(errors.phone)}
                        />
                      </div>
                      {touched.phone && errors.phone ? (
                        <p className={errorText}>{errors.phone}</p>
                      ) : null}
                    </div>

                    <div>
                      <label className={labelBase} htmlFor="email">Email</label>
                      <input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        className={fieldBase}
                        placeholder="patient@example.com"
                      />
                    </div>

                    <div>
                      <label className={labelBase} htmlFor="age">Age</label>
                      <input
                        id="age"
                        type="number"
                        value={formData.age}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            age: e.target.value,
                          })
                        }
                        className={fieldBase}
                        inputMode="numeric"
                        min={0}
                        placeholder="e.g. 28"
                        disabled={Boolean(formData.date_of_birth)}
                      />
                    </div>

                    <div>
                      <label className={labelBase} htmlFor="date_of_birth">Date of birth (optional)</label>
                      <input
                        id="date_of_birth"
                        type="date"
                        value={formData.date_of_birth}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            date_of_birth: e.target.value,
                          })
                        }
                        className={fieldBase}
                        disabled={Boolean(formData.age)}
                      />
                    </div>

                    <div>
                      <label className={labelBase} htmlFor="gender">Gender</label>
                      <select
                        id="gender"
                        value={formData.gender}
                        onChange={(e) =>
                          setFormData({ ...formData, gender: e.target.value })
                        }
                        className={fieldBase}
                      >
                        <option value="">Select gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>

                    <div>
                      <label className={labelBase} htmlFor="blood_type">Blood type</label>
                      <select
                        id="blood_type"
                        value={formData.blood_type}
                        onChange={(e) =>
                          setFormData({ ...formData, blood_type: e.target.value })
                        }
                        className={fieldBase}
                      >
                        <option value="">Select blood type</option>
                        {bloodTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </section>

                {/* Contact */}
                <section className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200/70">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <ShieldPlus className="h-4 w-4 text-[#235F72]" />
                    Contact details
                  </div>
                  <div className="mt-1 text-xs text-gray-600">Optional.</div>

                  <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label className={labelBase} htmlFor="address">Address</label>
                      <input
                        id="address"
                        type="text"
                        value={formData.address}
                        onChange={(e) =>
                          setFormData({ ...formData, address: e.target.value })
                        }
                        className={fieldBase}
                        placeholder="Full address"
                      />
                    </div>

                    <div>
                      <label className={labelBase} htmlFor="emergency_contact">Emergency contact</label>
                      <input
                        id="emergency_contact"
                        type="text"
                        value={formData.emergency_contact}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            emergency_contact: e.target.value,
                          })
                        }
                        className={fieldBase}
                        placeholder="Emergency contact number"
                      />
                    </div>
                  </div>
                </section>

                {/* Medical history */}
                <section className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200/70">
                  <button
                    type="button"
                    onClick={() => setMedicalOpen((p) => !p)}
                    className="flex w-full items-center justify-between gap-3 rounded-xl bg-white px-3 py-2 text-left ring-1 ring-gray-200 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                    aria-expanded={medicalOpen}
                  >
                    <div>
                      <div className="text-sm font-semibold text-gray-900">Medical history</div>
                      <div className="mt-0.5 text-xs text-gray-600">Optional — expand if needed.</div>
                    </div>
                    {medicalOpen ? (
                      <ChevronUp className="h-4 w-4 text-gray-600" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-600" />
                    )}
                  </button>

                  {medicalOpen ? (
                    <div className="mt-4 grid grid-cols-1 gap-4">
                      <div>
                        <label className={labelBase} htmlFor="known_allergies">Known allergies</label>
                        <textarea
                          id="known_allergies"
                          value={formData.known_allergies}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              known_allergies: e.target.value,
                            })
                          }
                          className={fieldBase}
                          placeholder="List any known allergies"
                          rows={2}
                        />
                      </div>

                      <div>
                        <label className={labelBase} htmlFor="chronic_conditions">Chronic conditions</label>
                        <textarea
                          id="chronic_conditions"
                          value={formData.chronic_conditions}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              chronic_conditions: e.target.value,
                            })
                          }
                          className={fieldBase}
                          placeholder="List any chronic conditions"
                          rows={2}
                        />
                      </div>

                      <div>
                        <label className={labelBase} htmlFor="current_medications">Current medications</label>
                        <textarea
                          id="current_medications"
                          value={formData.current_medications}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              current_medications: e.target.value,
                            })
                          }
                          className={fieldBase}
                          placeholder="List current medications"
                          rows={2}
                        />
                      </div>
                    </div>
                  ) : null}
                </section>
              </div>

              {/* Right rail: quick tips */}
              <aside className="min-w-0 space-y-4">
                <div className="rounded-2xl bg-white p-4 ring-1 ring-gray-200/70">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <KeyRound className="h-4 w-4 text-[#235F72]" />
                    Patient account
                  </div>
                  <div className="mt-1 text-xs text-gray-600">Created automatically on submit.</div>

                  <div className="mt-3 space-y-2 rounded-2xl bg-gray-50 p-3 ring-1 ring-gray-200/70">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                        Username
                      </div>
                      <div className="text-sm font-semibold text-gray-900">{cardNumber}</div>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                        Password
                      </div>
                      <div className="text-sm font-semibold text-gray-900">
                        {phoneTrimmed ? "Uses phone number" : "Uses card number"}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 text-xs text-gray-600">Share credentials only with the patient.</div>
                </div>

                <div className="rounded-2xl bg-white p-4 ring-1 ring-gray-200/70">
                  <div className="text-sm font-semibold text-gray-900">Quick check</div>
                  <ul className="mt-2 space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 inline-flex h-2 w-2 rounded-full bg-[#36F1A2]" />
                      Confirm spelling of the patient’s full name.
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 inline-flex h-2 w-2 rounded-full bg-[#36F1A2]" />
                      Phone number creates the patient login password.
                    </li>
                  </ul>
                </div>

                <div className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200/70">
                  <div className="text-sm font-semibold text-gray-900">Next</div>
                  <div className="mt-1 text-xs text-gray-600">You’ll select tests and answer dynamic questions.</div>
                </div>
              </aside>
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-gray-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-xl bg-white px-4 py-3 text-sm font-semibold text-gray-700 ring-1 ring-gray-200 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#235F72] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1a4a5a] focus:outline-none focus:ring-2 focus:ring-[#36F1A2] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {isLoading ? "Registering…" : "Register patient & continue"}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PatientRegistration;
